import { useState, useEffect } from "react";
import { getSlackChannels } from "../../api/AppLinkApi";
import DropdownLayout from "../../components/DropdownLayout";
import { useTranslation } from "react-i18next";
import { Hash, ChevronDown, Search, Check } from "lucide-react";

type SlackChannelPickerProps = {
  onChange: (channel: { value: string; label: string }) => void;
  channelName?: string;
  onError?: (errorMessage: string) => void;
  onLoadSuccess?: () => void;
};

const SlackChannelPicker = ({
  onChange,
  channelName = "",
  onError,
  onLoadSuccess,
}: SlackChannelPickerProps) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState<any>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>(channelName);
  const [searchValue, setSearchValue] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    getSlackChannels()
      .then((data) => {
        setChannels(data);
        setErrorMessage("");
        onLoadSuccess?.();
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || "Failed to load Slack channels";
        setErrorMessage(msg);
        onError?.(msg);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredChannels =
    searchValue === ""
      ? channels
      : channels.filter((ch: any) => ch.label.toLowerCase().includes(searchValue.toLowerCase()));

  const activator = (
    <div
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
        selectedChannel
          ? "border-gray-200 bg-white text-gray-900 hover:border-gray-300"
          : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Hash className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="truncate">
          {errorMessage
            ? t("Unable to load channels")
            : selectedChannel || t("Select a channel...")}
        </span>
      </div>
      <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
    </div>
  );

  if (channels.length === 0) {
    return activator;
  }

  return (
    <DropdownLayout
      fullWidth={true}
      activator={activator}
      positionClassName="left-0 origin-top-left"
    >
      <div className="w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
        {/* Search input */}
        <div className="p-2 border-b border-gray-100">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/10 transition-all">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="text"
              className="w-full text-sm bg-transparent outline-none placeholder:text-gray-400 text-gray-900"
              placeholder={t("Search channels...")}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Channel list */}
        <div className="max-h-56 overflow-y-auto py-1">
          {filteredChannels.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">
              {t("No channels found")}
            </div>
          ) : (
            filteredChannels.map((item: any) => {
              const isSelected = selectedChannel === item.label;
              return (
                <button
                  key={`channel_${item.value}`}
                  type="button"
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                    isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    setSelectedChannel(item.label);
                    onChange(item);
                  }}
                >
                  <Hash
                    className={`w-3.5 h-3.5 shrink-0 ${
                      isSelected ? "text-blue-500" : "text-gray-400"
                    }`}
                  />
                  <span className="truncate flex-1">{item.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </DropdownLayout>
  );
};

export default SlackChannelPicker;
